import type { CheckInTemplate, Entry, Tag, Track } from '@anytracker/core'
import type Dexie from 'dexie'
import type { Table } from 'dexie'

/**
 * The Dexie schema (T-203).
 *
 * Per ADR-0003 this is IndexedDB, not `localStorage`: the latter caps around
 * 5 MB, is synchronous, and stores only strings. This is the sole durable copy
 * of the user's data.
 */

/** Bumped whenever the stores or indexes below change. */
export const DB_VERSION = 1
export const DB_NAME = 'anytracker'

/**
 * How an entry is stored, which is not quite how it is modelled.
 *
 * `trackIds` is denormalised from `measurements` purely so there is something
 * to index. IndexedDB can build a multi-entry index over an **array of keys**,
 * but not over a property of objects inside an array — there is no way to say
 * "index `measurements[*].trackId`". Without this field, "every entry that
 * measured hunger" is a full scan of every entry ever recorded, which is the
 * single most common query in the app.
 *
 * It never leaves this module: `toStored` adds it and `fromStored` strips it,
 * so `Entry` in core stays free of a field that exists only because of how one
 * database indexes things. The conformance suite's "reads back an item
 * unchanged" case is what holds that line.
 */
export interface StoredEntry extends Entry {
  readonly trackIds: readonly string[]
}

export interface StoredSetting {
  readonly key: string
  readonly value: string
}

export function toStored(entry: Entry): StoredEntry {
  // Distinct ids: a multi-entry index over duplicates is wasted space, and an
  // entry measuring the same track twice is already invalid upstream.
  return { ...entry, trackIds: [...new Set(entry.measurements.map((m) => m.trackId))] }
}

export function fromStored(record: StoredEntry): Entry {
  const { trackIds: _trackIds, ...entry } = record
  return entry
}

export type AnytrackerDb = Dexie & {
  tracks: Table<Track, string>
  tags: Table<Tag, string>
  templates: Table<CheckInTemplate, string>
  entries: Table<StoredEntry, string>
  settings: Table<StoredSetting, string>
}

/**
 * Only what is queried is indexed.
 *
 * Every index costs write time and space, and this database is written to
 * several times a day for years. These four exist because T-107 issues exactly
 * these queries:
 *
 *   at.epochMs   range selection, and the ascending order every listing needs
 *   *trackIds    "entries that measured this track" — see StoredEntry above
 *   *tagIds      "entries carrying this tag"
 *   templateId   "entries from this check-in"
 */
export function defineSchema(db: Dexie): void {
  db.version(DB_VERSION).stores({
    tracks: 'id',
    tags: 'id',
    templates: 'id',
    entries: 'id, at.epochMs, *trackIds, *tagIds, templateId',
    settings: 'key',
  })
}

export const STORE_NAMES = ['tracks', 'tags', 'templates', 'entries', 'settings'] as const
